import { Component, computed, inject, input, InputSignal, TemplateRef, viewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import {
  AvatarBorderType,
  AvatarComponent
} from '@app/shared/components/group-avatars/avatar/proffeo-avatar.component';
import { ProjectIndustriesDataService } from '@app/shared/services/project-industries-data.service';
import { DialogService } from '@app/shared/services/shared/dialog.service';
import { AvatarSize } from '@app/shared/types/enums/avatar-size.enum';
import { ProjectIndustry } from '@app/shared/types/models/project-industry/project-industry.model';
import { User } from '@app/shared/types/models/user/user.model';
import {
  ProffeoUiTableAction,
  ProffeoUiTableActionEvent,
  ProffeoUiTableCellTemplateContext,
  ProffeoUiTableColumn,
  UiTableComponent
} from '@app/shared/ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'proffeo-project-industries-list',
  imports: [MatIconModule, TranslatePipe, MatMenuModule, AvatarComponent, UiTableComponent],
  templateUrl: './project-industries-list.component.html'
})
export class ProjectIndustriesListComponent {
  private readonly dialogService: DialogService = inject(DialogService);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly projectIndustriesService: ProjectIndustriesDataService = inject(ProjectIndustriesDataService);
  private readonly adminCellTemplate =
    viewChild<TemplateRef<ProffeoUiTableCellTemplateContext<ProjectIndustry>>>('adminCell');

  protected readonly AvatarSize = AvatarSize;
  protected readonly AvatarBorderType = AvatarBorderType;
  protected readonly columns = computed((): ProffeoUiTableColumn<ProjectIndustry>[] => [
    {
      columnId: 'name',
      columnName: this.translateService.instant('project-industries.table-headers.name'),
      dataType: 'string',
      accessor: projectIndustry => projectIndustry.industry().name,
      sortable: false,
      hideable: false,
      widthPx: 480,
      minWidthPx: 180
    },
    {
      columnId: 'administrator',
      columnName: this.translateService.instant('project-industries.table-headers.industry-admin'),
      dataType: 'object',
      accessor: projectIndustry => projectIndustry,
      sortFunction: () => 0,
      sortable: false,
      hideable: false,
      expandable: false,
      widthPx: 170,
      template: this.adminCellTemplate()
    }
  ]);
  protected readonly rowActions = computed((): ProffeoUiTableAction<ProjectIndustry>[] => [
    {
      id: 'delete',
      label: this.translateService.instant('project-industries.list-actions.delete'),
      icon: 'delete'
    }
  ]);

  public projectIndustries: InputSignal<ProjectIndustry[]> = input.required<ProjectIndustry[]>();
  public projectId: InputSignal<string> = input.required<string>();

  protected resolveRowId(projectIndustry: ProjectIndustry): string {
    return projectIndustry.id;
  }

  protected onTableAction(event: ProffeoUiTableActionEvent<ProjectIndustry>): void {
    if (event.actionId === 'delete') {
      this.openIndustryDeleteConfirmationDialog(event.row);
    }
  }

  protected selectIndustryAdmin(admin: User, projectIndustry: ProjectIndustry): void {
    this.dialogService
      .opensSelectIndustryAdminDialog(admin)
      .afterClosed()
      .subscribe(result => {
        if (result) {
          this.projectIndustriesService.setIndustryAdmin(this.projectId(), projectIndustry, result.selectedAdmin);
        }
      });
  }

  protected openIndustryDeleteConfirmationDialog(projectIndustry: ProjectIndustry): void {
    this.dialogService
      .openConfirmationDialog({
        title: this.translateService.instant('project-industries.modals.delete-modal-title'),
        desc: this.translateService.instant('project-industries.modals.delete-modal-description', {
          value: projectIndustry.industry().name
        })
      })
      .afterClosed()
      .subscribe(decision => {
        if (decision) {
          this.deleteIndustry(projectIndustry);
        }
      });
  }

  private deleteIndustry(projectIndustry: ProjectIndustry): void {
    this.projectIndustriesService.deleteIndustry(this.projectId(), projectIndustry);
  }
}
